import React from 'react';
import { ageLabel } from '../utils/modelAge';

/**
 * Today's Health, under each index: two facts, two rows (Apal 2026-11-03:
 * "two things we need to show, one is model code update, one info is about
 * reporting" — "code and maybe reporting data, those are the two terms").
 *
 *   Model           v6 · updated 3 Nov (2 days ago)
 *   Reporting data  v6 · updated 4 Nov · next ~9:45 PM
 *
 * - Model: the model live now and when it was last updated (a code change).
 * - Reporting data: MarketVibes' full history run with a model, sent after
 *   each close — it drives the Latest view (updated transactions, charts).
 *   It names the model it was run with, so the day the model changes reads
 *   "v5 · … · v6 update expected" until the new run arrives.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "3 Nov" (this year) / "3 Nov 2025" from a YYYY-MM-DD date. */
export function shortDate(iso, today = new Date()) {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return `${d} ${MONTHS[m - 1]}${y !== today.getFullYear() ? ` ${y}` : ''}`;
}

const fullDate = (iso) => shortDate(iso, new Date(0));   // always with the year

const istDay = (ts) => new Date(ts).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });   // YYYY-MM-DD
const istTime = (ts) => new Date(ts).toLocaleTimeString('en-US', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: '2-digit' });
const istStamp = (ts) => new Date(ts).toLocaleString('en-GB', { timeZone: 'Asia/Kolkata' });

/** "updated 3 Nov (2 days ago)" · "updated today" · "original model" — or null. */
export function modelUpdated({ since, original }, today = new Date()) {
  if (original) return 'original model';
  if (!since) return null;
  const age = ageLabel(since, today);
  return age === 'today' ? 'updated today' : `updated ${shortDate(since, today)} (${age})`;
}

/** What the Reporting data row says, and how loud. */
export function reportingState(item) {
  const live = item.model_revision ?? null;
  const ran = item.latest_model_revision ?? null;
  if (!item.latest_updated_at) return { kind: 'none' };
  if (item.latest_stale) return { kind: 'stale', ran };
  // run with another model than the live one: the new run hasn't arrived yet
  if (ran != null && live != null && ran !== live) return { kind: 'behind', ran, live };
  return { kind: 'current', ran };
}

export function ModelLines({ item, today }) {
  const now = today || new Date();
  const sym = item.symbol;
  const live = item.model_revision ?? 1;
  const updated = modelUpdated({ since: item.model_revision_since, original: item.model_revision_original }, now);
  const st = reportingState(item);
  const ver = st.ran != null ? `v${st.ran} · ` : '';
  const when = item.latest_updated_at ? shortDate(istDay(item.latest_updated_at), now) : null;
  const due = item.latest_due_at ? istTime(item.latest_due_at) : null;

  const about = `Reporting data: MarketVibes runs ${sym}'s full history with the model and sends it after each close. `
    + 'It drives the Latest view — updated signals, transactions and charts. Published keeps the trades as they really happened.';
  const received = item.latest_updated_at ? `\nLast received ${istStamp(item.latest_updated_at)} IST.` : '';

  let data;
  if (st.kind === 'none') {
    data = (
      <span data-testid={`reporting-${sym}`} className="text-amber-400" title={`${about}\nNothing received for ${sym} yet.`}>
        ⚠ not received yet
      </span>
    );
  } else if (st.kind === 'stale') {
    data = (
      <span data-testid={`reporting-${sym}`} className="text-amber-400"
        title={`${about}${received}\nToday's update for the last session has not arrived — the Latest view is out of date.`}>
        ⚠ {ver}last updated {when} · today's update missing
      </span>
    );
  } else if (st.kind === 'behind') {
    data = (
      <span data-testid={`reporting-${sym}`} className="text-dark-200"
        title={`${about}${received}\nIt was run with model v${st.ran}; the model is now v${st.live}. The Latest view shows v${st.ran} until MarketVibes sends the v${st.live} run.`}>
        {ver}updated {when}
        <span data-testid="latest-due" className="text-dark-500 whitespace-nowrap">
          {due ? ` · v${st.live} update expected ~${due}` : ` · waiting for v${st.live}`}
        </span>
      </span>
    );
  } else {
    data = (
      <span data-testid={`reporting-${sym}`} title={`${about}${received}${due ? `\nNext update scheduled ~${due} IST — not late.` : ''}`}>
        {ver}updated {when}
        {due && <span data-testid="latest-due" className="text-dark-500 whitespace-nowrap"> · next ~{due}</span>}
      </span>
    );
  }

  return (
    <div data-testid={`model-lines-${sym}`} className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-dark-400">
      <span className="text-dark-500">Model</span>
      <span data-testid={`model-${sym}`}
        title={`The model live for ${sym}: v${live}${item.model_revision_since ? `, updated ${fullDate(item.model_revision_since)}` : ''}. Live signals and Published trades use it from that day.`}>
        v{live}{updated && <> · <span data-testid={`model-age-${sym}`}>{updated}</span></>}
      </span>
      <span className="text-dark-500">Reporting data</span>
      {data}
    </div>
  );
}

export default ModelLines;
